// Sticky bulk-action bar — appears when ≥1 task is selected (Manager+/Admin).
// Set status / priority / reassign / delete (confirm) / export. Every action
// routes through the parent so it can persist + refresh + audit.
import { useState } from "react";
import { Trash2, Download, X, Loader2, CheckSquare } from "lucide-react";
import BulkExtras from "./BulkExtras";

const PRIORITIES = [
  { key: "low", label: "Low" }, { key: "medium", label: "Medium" },
  { key: "high", label: "High" }, { key: "urgent", label: "Urgent" },
];

export default function BulkActionBar({ count, statuses = [], users = [], onPatch, onDelete, onExport, onClear, ids, teams, onDone }) {
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  return (
    <div className="sticky top-2 z-30 flex flex-wrap items-center gap-2 rounded-xl border border-teal-400/30 bg-slate-900/95 backdrop-blur px-3 py-2 shadow-xl" data-testid="ws-bulk-bar">
      <span className="inline-flex items-center gap-1.5 text-[12px] font-mono text-teal-300 mr-1">
        <CheckSquare className="w-3.5 h-3.5" /> {count} selected
      </span>

      <select disabled={busy} defaultValue="" onChange={(e) => e.target.value && run(() => onPatch({ status: e.target.value })).then(() => (e.target.value = ""))}
        className="ws-input max-w-[130px] text-[12px]" data-testid="ws-bulk-status">
        <option value="">Set status…</option>
        {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
      </select>

      <select disabled={busy} defaultValue="" onChange={(e) => e.target.value && run(() => onPatch({ priority: e.target.value })).then(() => (e.target.value = ""))}
        className="ws-input max-w-[130px] text-[12px]" data-testid="ws-bulk-priority">
        <option value="">Set priority…</option>
        {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
      </select>

      <select disabled={busy} defaultValue="" onChange={(e) => e.target.value && run(() => onPatch({ assignee_id: e.target.value })).then(() => (e.target.value = ""))}
        className="ws-input max-w-[150px] text-[12px]" data-testid="ws-bulk-assignee">
        <option value="">Reassign to…</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
      </select>

      {ids?.length > 0 && <BulkExtras ids={ids} teams={teams} onDone={onDone} />}

      {onExport && (
        <button disabled={busy} onClick={() => onExport()} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[12px] border border-white/10 text-slate-300 hover:bg-white/5" data-testid="ws-bulk-export">
          <Download className="w-3.5 h-3.5" /> Export
        </button>
      )}

      <button disabled={busy} onClick={() => setConfirmDel(true)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[12px] bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-40" data-testid="ws-bulk-delete">
        <Trash2 className="w-3.5 h-3.5" /> Delete
      </button>

      <div className="ml-auto flex items-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin text-teal-300" />}
        <button onClick={onClear} className="text-slate-400 hover:text-white p-1" title="Clear selection" data-testid="ws-bulk-clear">
          <X className="w-4 h-4" />
        </button>
      </div>

      {confirmDel && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setConfirmDel(false)}>
          <div className="ws-card p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="ws-bulk-delete-confirm">
            <h3 className="text-white font-semibold mb-2">Delete {count} task{count > 1 ? "s" : ""}?</h3>
            <p className="text-slate-400 text-sm mb-4">This can’t be undone. Tasks you don’t manage will be skipped.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(false)} className="px-3 py-1.5 rounded text-slate-300 hover:bg-white/5 text-[12px]">Cancel</button>
              <button onClick={() => { setConfirmDel(false); run(onDelete); }} className="px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-500 text-white text-[12px]" data-testid="ws-bulk-delete-confirm-btn">
                Delete {count}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
